// Typography preset metadata for the DesignPanel picker + live preview.
// MIRRORS backend/src/render/typography.ts — keep ids/roles in sync. The backend
// is authoritative for the published site; this copy drives the admin UI only.

export type Script = 'dv' | 'en';

// CSS font-family names (declared in styles.css as "WS …" @font-face rules).
const FAM: Record<string, string> = {
  faruma: "'WS Faruma'",
  aammu: "'WS Aammu'",
  waheed: "'WS Waheed'",
  notothaana: "'WS Noto Thaana'",
  dmsans: "'WS DM Sans'",
  inter: "'WS Inter'",
  manrope: "'WS Manrope'",
  lora: "'WS Lora'",
  space: "'WS Space Grotesk'",
};

export interface TypePreset {
  id: string;
  script: Script;
  name: string; // English label for the picker
  heading: string; // CSS family
  sub: string;
  body: string;
}

function p(id: string, script: Script, name: string, h: string, s: string, b: string): TypePreset {
  return { id, script, name, heading: FAM[h]!, sub: FAM[s]!, body: FAM[b]! };
}

export const PRESETS: TypePreset[] = [
  // Dhivehi / Thaana
  p('dv-classic', 'dv', 'Classic', 'aammu', 'faruma', 'faruma'),
  p('dv-clean', 'dv', 'Clean', 'faruma', 'faruma', 'faruma'),
  p('dv-modern', 'dv', 'Modern', 'notothaana', 'notothaana', 'notothaana'),
  p('dv-elegant', 'dv', 'Elegant', 'aammu', 'notothaana', 'notothaana'),
  p('dv-heritage', 'dv', 'Heritage', 'waheed', 'faruma', 'faruma'),
  p('dv-editorial', 'dv', 'Editorial', 'waheed', 'notothaana', 'notothaana'),
  // English / Latin
  p('en-geometric', 'en', 'Geometric', 'dmsans', 'dmsans', 'dmsans'),
  p('en-modern', 'en', 'Modern', 'space', 'inter', 'inter'),
  p('en-humanist', 'en', 'Humanist', 'manrope', 'manrope', 'inter'),
  p('en-editorial', 'en', 'Editorial', 'lora', 'inter', 'inter'),
  p('en-neutral', 'en', 'Neutral', 'inter', 'inter', 'inter'),
  p('en-display', 'en', 'Display', 'space', 'space', 'manrope'),
];

export const DEFAULT_PRESET: Record<Script, string> = { dv: 'dv-classic', en: 'en-geometric' };

export function presetsFor(script: Script): TypePreset[] {
  return PRESETS.filter((x) => x.script === script);
}

export function resolvePreset(script: Script, id: string | undefined): TypePreset {
  return PRESETS.find((x) => x.script === script && x.id === id) ?? PRESETS.find((x) => x.id === DEFAULT_PRESET[script])!;
}

// Sample text for the live preview, per script.
export const SAMPLE: Record<Script, { heading: string; sub: string; body: string }> = {
  dv: {
    heading: 'ކައުންސިލްގެ ރަސްމީ ވެބްސައިޓް',
    sub: 'ހާމަކުރަންޖެހޭ މަޢުލޫމާތު',
    body: 'މަޢުލޫމާތު ހޯދައި ލިބިގަތުމުގެ ޙައްޤުގެ ޤާނޫނުގެ ދަށުން، މި އިދާރާއިން ހާމަކުރަންޖެހޭ މަޢުލޫމާތު މި ވެބްސައިޓުގައި ޝާއިޢުކުރެއެވެ.',
  },
  en: {
    heading: 'Official Council Website',
    sub: 'Information we disclose',
    body: 'The information this office is required to publish under the Right to Information Act, presented clearly for every resident.',
  },
};
